import React, { useState, useEffect } from "react";
import {
  fetchPromoterRequests,
  updatePromoterRequestStatus,
} from "../../services/promoterRequestService";
import { Icon } from "@iconify/react";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button } from "react-bootstrap";

const VenuePromotionRequestListLayer = ({ requestType = "host" }) => {
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [commissionTypeFilter, setCommissionTypeFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [modalMode, setModalMode] = useState("approve"); // approve | edit
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [commissionType, setCommissionType] = useState("percentage");
  const [commissionValue, setCommissionValue] = useState("");
  const [commissionErrors, setCommissionErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isCommissionSort = sortColumn === "commissionValue";
  const showEventName = requestType === "event";
  const tableColSpan = showEventName ? 8 : 7;

  const getCommissionType = (item) => {
    const raw =
      item?.commissionType ?? item?.commission_type ?? "";
    const normalized = String(raw).toLowerCase().replace(/[\s_-]/g, "");
    if (normalized === "flatfee" || normalized === "flat") return "flatfee";
    if (normalized === "percentage" || normalized === "percent")
      return "percentage";
    return "percentage";
  };

  // Request type: API uses host/event; UI labels are Club/Event
  const getRequestType = (item) => {
    const raw =
      item?.type ?? item?.requestType ?? item?.venueType ?? item?.listingType ?? "";
    const normalized = String(raw).toLowerCase().replace(/[\s_-]/g, "");
    if (normalized === "host" || normalized === "club") return "host";
    if (normalized === "event") return "event";
    return "";
  };

  const getRequestTypeLabel = (item) => {
    const type = getRequestType(item);
    if (type === "host") return "Club";
    if (type === "event") return "Event";
    return "N/A";
  };

  const getHostName = (item) => {
    const name =
      item?.hostName ||
      item?.host?.fullName ||
      item?.host?.name ||
      item?.clubName ||
      item?.userId?.fullName ||
      "";
    return name || "-";
  };

  const getEventName = (item) => {
    const name = item?.eventName || item?.event?.name || "";
    return name || "-";
  };

  const getCommissionValue = (item) => {
    const raw =
      item?.commissionValue ?? item?.commission_value ?? item?.value ?? null;
    if (raw == null || raw === "") return "";
    return String(raw);
  };

  const getNumericCommissionValue = (item) => {
    const raw = getCommissionValue(item);
    if (raw === "") return null;
    // Strip %, currency, etc. so "8%" / "9.99" both parse
    const cleaned = String(raw).replace(/[^0-9.-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const sortByCommissionValue = (list, order) => {
    return [...list].sort((a, b) => {
      const aVal = getNumericCommissionValue(a);
      const bVal = getNumericCommissionValue(b);
      const aIsNA = aVal == null;
      const bIsNA = bVal == null;

      // ASC: numbers first (low→high), N/A last
      // DESC: N/A first, then numbers (high→low)
      if (aIsNA && bIsNA) return 0;
      if (aIsNA) return order === "asc" ? 1 : -1;
      if (bIsNA) return order === "asc" ? -1 : 1;

      return order === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  const mergeCommissionPreserve = (incoming, prev) =>
    incoming.map((row) => {
      const existing = prev.find((p) => p._id === row._id);
      const apiValue =
        row?.commissionValue ?? row?.commission_value ?? row?.value;
      if (
        (apiValue == null || apiValue === "") &&
        existing &&
        existing.commissionValue != null &&
        existing.commissionValue !== ""
      ) {
        return {
          ...row,
          commissionType: existing.commissionType,
          commissionValue: existing.commissionValue,
        };
      }
      return row;
    });

  const fetchData = async () => {
    try {
      setLoading(true);

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      // Commission value: numeric sort on frontend (backend sort is unreliable)
      if (isCommissionSort) {
        const res = await fetchPromoterRequests({
          page: 1,
          limit: 99999,
          search: searchQuery,
          sortBy: "createdAt",
          sortOrder: "desc",
          status: statusFilter,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          commissionType: commissionTypeFilter,
          type: requestType,
        });

        const incoming = mergeCommissionPreserve(res.data?.data || [], items);
        const sorted = sortByCommissionValue(incoming, sortOrder);
        setTotalItems(sorted.length);
        setTotalPages(Math.max(1, Math.ceil(sorted.length / itemsPerPage)));
        const start = (currentPage - 1) * itemsPerPage;
        setItems(sorted.slice(start, start + itemsPerPage));
        return;
      }

      const res = await fetchPromoterRequests({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder,
        status: statusFilter,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        commissionType: commissionTypeFilter,
        type: requestType,
      });

      setItems(mergeCommissionPreserve(res.data?.data || [], items));
      setTotalItems(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Failed to fetch promotion requests",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    commissionTypeFilter,
    requestType,
    startDate,
    endDate,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      // First click on commission → ASC (low to high); other cols → DESC
      setSortOrder(column === "commissionValue" ? "asc" : "desc");
    }
    setCurrentPage(1);
  };

  const hasCommissionValue = (item) => getCommissionValue(item) !== "";

  const openCommissionModal = (item, mode = "approve") => {
    setSelectedRequest(item);
    setModalMode(mode);
    setCommissionType(getCommissionType(item));
    setCommissionValue(getCommissionValue(item));
    setCommissionErrors({});
    setShowCommissionModal(true);
  };

  const closeCommissionModal = () => {
    setShowCommissionModal(false);
    setSelectedRequest(null);
    setCommissionType("percentage");
    setCommissionValue("");
    setCommissionErrors({});
    setSubmitting(false);
  };

  const handleCommissionValueChange = (value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const normalized =
      parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];

    const maxLen = commissionType === "percentage" ? 3 : 10;
    if (normalized.length > maxLen) {
      return;
    }

    setCommissionValue(normalized);
    setCommissionErrors((prev) => ({
      ...prev,
      commissionValue: "",
    }));
  };

  const validateCommission = () => {
    const errors = {};
    if (!commissionType) {
      errors.commissionType = "Commission type is required";
    }
    if (
      commissionValue === "" ||
      commissionValue == null ||
      Number.isNaN(Number(commissionValue))
    ) {
      errors.commissionValue = "Commission value is required";
    } else {
      const value = Number(commissionValue);
      if (value <= 0) {
        errors.commissionValue = "Commission value must be greater than zero";
      } else if (commissionType === "percentage") {
        if (value < 1) {
          errors.commissionValue = "Percentage must be at least 1";
        } else if (value > 100) {
          errors.commissionValue = "Percentage cannot be more than 100";
        }
      } else if (commissionType === "flatfee") {
        if (commissionValue.length > 10) {
          errors.commissionValue = "Flat fee cannot exceed 10 characters";
        }
      }
    }
    setCommissionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCommissionSubmit = async () => {
    if (!selectedRequest?._id || !validateCommission()) return;

    setSubmitting(true);
    try {
      const payload = {
        status: "approved",
        commissionType,
        commissionValue: String(commissionValue),
      };
      const response = await updatePromoterRequestStatus(
        selectedRequest._id,
        payload,
      );

      // Keep commission on the row immediately so Edit modal can prefill
      setItems((prev) =>
        prev.map((row) =>
          row._id === selectedRequest._id
            ? {
                ...row,
                status: "approved",
                commissionType,
                commissionValue: String(commissionValue),
              }
            : row,
        ),
      );

      setSuccessMessage(
        response?.data?.message ||
          (modalMode === "edit"
            ? "Commission updated successfully"
            : "Request approved successfully"),
      );
      closeCommissionModal();
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save commission",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (id) => {
    const result = await showConfirmationDialog(
      "This action will reject the request.",
      "Yes, reject it!",
    );
    if (!result.isConfirmed) return;

    try {
      const response = await updatePromoterRequestStatus(id, {
        status: "rejected",
      });
      setSuccessMessage(
        response?.data?.message || "Request rejected successfully",
      );
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to reject request",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getStatusClass = (status) => {
    if (status === "approved") {
      return "bg-success-focus text-success-600 border border-success-main";
    }
    if (status === "rejected") {
      return "bg-danger-focus text-danger-600 border border-danger-main";
    }
    return "bg-warning-focus text-warning-600 border border-warning-main";
  };

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }

    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setEndDate(null);
                setCurrentPage(1);
              }}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              minDate={startDate}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>

          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={commissionTypeFilter}
            onChange={(e) => {
              setCommissionTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Commission Type</option>
            <option value="flatfee">Flat Fee</option>
            <option value="percentage">Percentage</option>
          </select>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("host.fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Host Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "host.fullName" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "host.fullName" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                {showEventName && (
                  <th
                    onClick={() => handleSort("event.name")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      Event Name
                      <span className="sort-colume">
                        <Icon
                          icon="mdi:arrow-up"
                          className={`arrow ${
                            sortColumn === "event.name" && sortOrder === "asc"
                              ? "active"
                              : ""
                          }`}
                        />
                        <Icon
                          icon="mdi:arrow-down"
                          className={`arrow ${
                            sortColumn === "event.name" && sortOrder === "desc"
                              ? "active"
                              : ""
                          }`}
                        />
                      </span>
                    </div>
                  </th>
                )}
                <th
                  onClick={() => handleSort("promoter.fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Promoter Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "promoter.fullName" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "promoter.fullName" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("type")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Type
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "type" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "type" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Request Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("commissionValue")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Commission
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "commissionValue" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "commissionValue" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>{getHostName(item)}</td>
                    {showEventName && <td>{getEventName(item)}</td>}
                    <td>{item.promoterName || "N/A"}</td>
                    <td>{getRequestTypeLabel(item)}</td>
                    <td>
                      <span
                        className={`${getStatusClass(
                          item.status,
                        )} px-24 py-4 radius-4 fw-medium text-sm`}
                      >
                        {item.status
                          ? item.status.charAt(0).toUpperCase() +
                            item.status.slice(1)
                          : "N/A"}
                      </span>
                    </td>
                    <td>{formatDate(item.requestDate)}</td>
                    <td>
                      {hasCommissionValue(item)
                        ? `${
                            getCommissionType(item) === "flatfee"
                              ? "Flat Fee"
                              : "Percentage"
                          }: ${getCommissionValue(item)}${
                            getCommissionType(item) === "flatfee" ? "" : "%"
                          }`
                        : "N/A"}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {(item.status === "pending" &&
                          <button
                            type="button"
                            title="Approve"
                            onClick={() => openCommissionModal(item, "approve")}
                            className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:check" className="icon text-xl" />
                          </button>
                        )}
                        {item.status === "pending" && (
                          <button
                            type="button"
                            title="Reject"
                            onClick={() => handleReject(item._id)}
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:close" className="icon text-xl" />
                          </button>
                        )}
                        {item.status === "approved" && (
                          <button
                            type="button"
                            title="Edit Commission"
                            onClick={() => openCommissionModal(item, "edit")}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {item.status === "rejected" && (
                           <span className="fw-medium"> - </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() =>
                  currentPage > 1 && setCurrentPage((prev) => prev - 1)
                }
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() =>
                  currentPage < totalPages && setCurrentPage((prev) => prev + 1)
                }
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>

      <Modal show={showCommissionModal} onHide={closeCommissionModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "edit" ? "Edit Commission" : "Approve Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Commission Type<span className="text-danger-600">*</span>
            </label>
            <select
              className={`form-select ${
                commissionErrors.commissionType ? "is-invalid" : ""
              }`}
              value={commissionType}
              onChange={(e) => {
                setCommissionType(e.target.value);
                setCommissionErrors((prev) => ({
                  ...prev,
                  commissionType: "",
                }));
              }}
            >
              <option value="percentage">Percentage</option>
              <option value="flatfee">Flat Fee</option>
            </select>
            {commissionErrors.commissionType && (
              <div className="text-danger mt-1">
                {commissionErrors.commissionType}
              </div>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Commission Value {commissionType === "percentage" ? "(%)" : "($)"}
              <span className="text-danger-600">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                commissionErrors.commissionValue ? "is-invalid" : ""
              }`}
              placeholder={
                commissionType === "percentage"
                  ? "Enter percentage"
                  : "Enter flat fee"
              }
              value={commissionValue}
              onChange={(e) => handleCommissionValueChange(e.target.value)}
            />
            {commissionErrors.commissionValue && (
              <div className="text-danger mt-1">
                {commissionErrors.commissionValue}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCommissionModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCommissionSubmit}
            disabled={submitting}
          >
            {submitting
              ? "Saving..."
              : modalMode === "edit"
                ? "Update"
                : "Approve"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VenuePromotionRequestListLayer;
